import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button, styled, useTheme } from "@monolith-forensics/monolith-ui";
import { PackageIcon } from "lucide-react";

import {
  CreateLabAssetModal,
  LAB_ASSET_TYPE_LABELS,
  LabAssetsTable,
  LabAssetsTableHandle,
  LabAssetType,
} from "@/Features";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatures } from "@/contexts/FeaturesContext";
import AccessDeniedMessage from "@/components/AccessDeniedMessage.js";
import Page404 from "@/pages/auth/Page404";

// Header mirrors EvidencePage (icon title + type toggle); the create button
// lives in the mfui table menu like EquipmentSection. SoftwareSection is still
// the legacy MUI page.
const StyledPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledPageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const StyledPageTitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  width: fit-content;
  padding: 2px 4px;
  font-weight: bold;
  font-size: 1rem;
`;

// Same look as EvidencePage's All / My Cases / Assigned to Me toggle.
const ViewButton = styled(Button)<{ $active: boolean }>`
  font-weight: 525;

  &,
  & > * {
    opacity: ${({ $active }) => ($active ? 1 : 0.85)} !important;
    text-decoration: none !important;
  }
`;

// undefined = all types; the table drops an undefined asset_type from the
// query body.
const TYPE_VIEWS: { label: string; value: LabAssetType | undefined }[] = [
  { label: "All", value: undefined },
  { label: LAB_ASSET_TYPE_LABELS.equipment, value: "equipment" },
  { label: LAB_ASSET_TYPE_LABELS.software, value: "software" },
];

// Same spacing EquipmentTable puts around its mfui Table.
const StyledTableWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  margin: 10px 0;
`;

// Versioned key: the table persists column state plus filter rules, search
// text, and sort to localStorage under this key. Bump the suffix whenever the
// persisted shape changes so users get fresh defaults instead of a stale blob
// (same approach as storage:view:v2). v2: the Asset Type filter def was
// dropped in favour of the toggle, and a v1 blob may still hold a rule that
// would AND against it. ENG-2295 restored the def for the All view only (see
// LabAssetFilterDefs) without a v3 bump: no v2 blob can hold an asset_type
// rule, and LabAssetsTable drops one on a typed view anyway.
const STATE_KEY = "lab-assets:view:v2";
// Sibling key for the toggle (casesView pattern); suffixed to STATE_KEY so a
// future bump clears both. Validated on read: an unknown value would reach
// the strict POST /assets/query schema and 400.
const TYPE_KEY = `${STATE_KEY}:assetType`;

const readAssetType = (): LabAssetType | undefined => {
  const v = localStorage.getItem(TYPE_KEY);
  return v === "software" || v === "equipment" ? v : undefined;
};

const LabAssetsSectionContent = () => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const theme = useTheme();
  const tableRef = useRef<LabAssetsTableHandle>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assetType, setAssetType] = useState<LabAssetType | undefined>(
    readAssetType
  );

  const handleTypeChange = (value: LabAssetType | undefined) => {
    setAssetType(value);
    if (value) localStorage.setItem(TYPE_KEY, value);
    else localStorage.removeItem(TYPE_KEY);
  };

  const canCreate = hasPermission(MONOLITH_PERMISSIONS.ASSET_CREATE);

  return (
    <StyledPage>
      <Helmet title="Assets" />
      <StyledPageHeader>
        <StyledPageTitle>
          <PackageIcon color={theme.palette.primary.main} size={18} />{" "}
          <span>Assets</span>
        </StyledPageTitle>
        {TYPE_VIEWS.map(({ label, value }) => (
          <ViewButton
            key={label}
            variant={assetType === value ? "contained" : "outlined"}
            size="xxs"
            $active={assetType === value}
            onClick={() => handleTypeChange(value)}
          >
            {label}
          </ViewButton>
        ))}
      </StyledPageHeader>
      <StyledTableWrap>
        <LabAssetsTable
          tableRef={tableRef}
          query={{ asset_type: assetType }}
          stateStoreKey={STATE_KEY}
          onCreateClick={canCreate ? () => setShowCreateModal(true) : undefined}
        />
      </StyledTableWrap>
      {canCreate && (
        <CreateLabAssetModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          // The modal closes itself; the parent only refreshes the list.
          onSubmit={() => tableRef.current?.refresh()}
        />
      )}
    </StyledPage>
  );
};

const LabAssetsSection = () => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();

  if (!hasFeatures([FEATURES.LAB_ASSETS])) {
    return <Page404 />;
  }

  if (!hasPermission(MONOLITH_PERMISSIONS.ASSET_READ_ALL)) {
    return <AccessDeniedMessage />;
  }

  return <LabAssetsSectionContent />;
};

export default LabAssetsSection;
